import { z } from 'zod';

/**
 * Instants are exchanged as UTC ISO 8601 strings with an explicit `Z` offset,
 * e.g. `2025-01-21T13:07:00.000Z`. Local wall time is derived using the family time zone.
 */
export const utcInstantSchema = z.iso
    .datetime({ offset: false, precision: 3 })
    .describe('UTC instant in ISO 8601 format with millisecond precision and a Z suffix');

export type UtcInstant = z.infer<typeof utcInstantSchema>;

/**
 * Calendar dates with no time component, e.g. `2025-01-17`.
 */
export const calendarDateSchema = z.iso.date().describe('Calendar date in YYYY-MM-DD format');

export type CalendarDate = z.infer<typeof calendarDateSchema>;

const timeZoneCache = new Map<string, boolean>();

/**
 * Returns true when the runtime's Intl implementation recognises the IANA time zone name.
 */
export function isValidTimeZone(value: string): boolean {
    const cached = timeZoneCache.get(value);
    if (cached !== undefined) {
        return cached;
    }
    const valid = canFormatInTimeZone(value);
    timeZoneCache.set(value, valid);
    return valid;
}

function canFormatInTimeZone(timeZone: string): boolean {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    } catch {
        return false;
    }
}

/**
 * IANA time zone identifier such as `Australia/Brisbane`.
 * Validated against the runtime's Intl data rather than a hard-coded list.
 */
export const ianaTimeZoneSchema = z
    .string()
    .min(1)
    .max(64)
    .refine(isValidTimeZone, { message: 'Unknown IANA time zone' });

export type IanaTimeZone = z.infer<typeof ianaTimeZoneSchema>;

/**
 * Converts a Date to the canonical UTC instant string.
 */
export function toUtcInstant(date: Date): UtcInstant {
    return date.toISOString();
}

export interface ZonedDayBounds {
    /** Inclusive start of the calendar day in `timeZone`, as a UTC instant. */
    from: UtcInstant;
    /** Exclusive end (start of the next calendar day) in `timeZone`, as a UTC instant. */
    to: UtcInstant;
}

/**
 * UTC instants covering one calendar day in an IANA time zone.
 * `to` is exclusive, so a half-open range `[from, to)` contains that local day.
 */
/** Calendar date (`YYYY-MM-DD`) of `instant` in `timeZone`. */
export function calendarDateInZone(instant: Date, timeZone: IanaTimeZone): CalendarDate {
    const formatted = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant);
    const parsed = calendarDateSchema.safeParse(formatted);
    if (!parsed.success) {
        throw new Error(`Could not format a calendar date in ${timeZone}`);
    }
    return parsed.data;
}

/** Moves a `YYYY-MM-DD` date by whole calendar days, ignoring the clock and daylight saving. */
export function shiftCalendarDate(date: CalendarDate, days: number): CalendarDate {
    const parts = parseCalendarDate(date);
    const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
    return calendarDateSchema.parse(shifted.toISOString().slice(0, 10));
}

/**
 * Inclusive calendar dates from `from` through `to`.
 * Stops after 43 dates so a caller can reject an over-long range.
 */
export function enumerateCalendarDates(from: CalendarDate, to: CalendarDate): CalendarDate[] {
    if (from > to) {
        return [];
    }
    const dates: CalendarDate[] = [];
    let cursor = from;
    while (cursor <= to && dates.length < 43) {
        dates.push(cursor);
        cursor = shiftCalendarDate(cursor, 1);
    }
    return dates;
}

export function zonedDayBounds(date: CalendarDate, timeZone: IanaTimeZone): ZonedDayBounds {
    const start = parseCalendarDate(date);
    const end = nextCalendarDate(start);
    return {
        from: toUtcInstant(zonedMidnightToUtc(start, timeZone)),
        to: toUtcInstant(zonedMidnightToUtc(end, timeZone)),
    };
}

/** Wall-clock `HH:mm` on a calendar date in `timeZone`, as a UTC instant. */
export function zonedDateTimeToUtc(
    date: CalendarDate,
    timeOfDay: string,
    timeZone: IanaTimeZone,
): UtcInstant {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(timeOfDay);
    const hour = match?.[1];
    const minute = match?.[2];
    if (hour === undefined || minute === undefined) {
        throw new Error(`Invalid time of day "${timeOfDay}"`);
    }
    return toUtcInstant(
        wallClockToUtc(parseCalendarDate(date), Number(hour), Number(minute), timeZone),
    );
}

interface CalendarParts {
    year: number;
    month: number;
    day: number;
}

function parseCalendarDate(date: CalendarDate): CalendarParts {
    const [year, month, day] = date.split('-').map((part) => Number(part));
    if (year === undefined || month === undefined || day === undefined) {
        throw new Error(`Invalid calendar date "${date}"`);
    }
    return { year, month, day };
}

function nextCalendarDate(parts: CalendarParts): CalendarParts {
    const next = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + 1));
    return {
        year: next.getUTCFullYear(),
        month: next.getUTCMonth() + 1,
        day: next.getUTCDate(),
    };
}

/** Wall-clock midnight in `timeZone`, as a UTC Date. */
function zonedMidnightToUtc(parts: CalendarParts, timeZone: string): Date {
    return wallClockToUtc(parts, 0, 0, timeZone);
}

function wallClockToUtc(
    parts: CalendarParts,
    hour: number,
    minute: number,
    timeZone: string,
): Date {
    const guess = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, hour, minute, 0));
    const first = guess.getTime() - timeZoneOffsetMs(guess, timeZone);
    return new Date(guess.getTime() - timeZoneOffsetMs(new Date(first), timeZone));
}

/**
 * Milliseconds to add to a UTC instant to reach the same numbers on the wall clock in `timeZone`.
 * Positive east of UTC.
 */
function timeZoneOffsetMs(instant: Date, timeZone: string): number {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(instant);
    const value: Record<string, string> = {};
    for (const part of parts) {
        if (part.type !== 'literal') {
            value[part.type] = part.value;
        }
    }
    let hour = Number(value.hour);
    let day = Number(value.day);
    let month = Number(value.month);
    let year = Number(value.year);
    // Some engines report midnight as hour 24 on the previous calendar day.
    if (hour === 24) {
        hour = 0;
        const rolled = new Date(Date.UTC(year, month - 1, day + 1));
        year = rolled.getUTCFullYear();
        month = rolled.getUTCMonth() + 1;
        day = rolled.getUTCDate();
    }
    const asUtc = Date.UTC(year, month - 1, day, hour, Number(value.minute), Number(value.second));
    return asUtc - instant.getTime();
}
